import {
  Controller,
  Control,
  FieldValues,
  Path,
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
  UseFormClearErrors,
  UseFormSetError,
} from 'react-hook-form';
import { FormFieldConfig } from './locationSchemas';

interface DynamicFieldProps<T extends FieldValues = FieldValues> {
  field: FormFieldConfig;
  control: Control<T>;
  name: Path<T>;
  className?: string;
  register?: UseFormRegister<T>;
  errors?: FieldErrors<T>;
  setValue?: UseFormSetValue<T>;
  watch?: UseFormWatch<T>;
  clearErrors?: UseFormClearErrors<T>;
  setError?: UseFormSetError<T>;
  disabled?: boolean;
}

export default function DynamicField<T extends FieldValues = FieldValues>({
  field,
  control,
  name,
  className = '',
  disabled = false,
  register,
  errors,
  setValue,
  watch,
  clearErrors,
  setError,
}: DynamicFieldProps<T>) {
  const baseInputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const asterisk = field.required ? <span className="text-red-500">*</span> : null;

  const buildRules = () => {
    const rules: Record<string, unknown> = {};
    if (field.required) rules.required = `${field.label} is required`;

    if (field.validation) {
      const v = field.validation;
      if (v.minLength)
        rules.minLength = { value: v.minLength, message: `Min ${v.minLength} characters` };
      if (v.maxLength)
        rules.maxLength = { value: v.maxLength, message: `Max ${v.maxLength} characters` };
      if (v.min !== undefined) rules.min = { value: v.min, message: `Min value is ${v.min}` };
      if (v.max !== undefined) rules.max = { value: v.max, message: `Max value is ${v.max}` };
      if (v.pattern) {
        rules.pattern = {
          value: new RegExp(v.pattern),
          message: v.errorMessage || 'Invalid format',
        };
      }
      if (v.validate) {
        rules.validate = v.validate;
      }
    }
    return rules;
  };

  const error = errors?.[name];

  return (
    <Controller
      control={control}
      name={name}
      rules={buildRules()}
      render={({ field: { value, onChange, onBlur } }) => {
        return (
          <div className={`flex flex-col ${className}`}>
            {field.type === 'tags' ? (
              <div className="flex flex-col w-full min-w-0">
                <label className={labelClass}>
                  {field.label} {asterisk}
                </label>
                <div
                  className={`
                    flex flex-wrap gap-2 p-2 border rounded-lg bg-white min-h-[42px] 
                    transition-all focus-within:ring-2 focus-within:ring-blue-500 
                    w-full max-w-full overflow-hidden
                    ${error ? 'border-red-500' : 'border-gray-300'}
                  `}
                >
                  {(Array.isArray(value) ? (value as string[]) : []).map((tag, index) => (
                    <span
                      key={`${tag}-${index}`}
                      className="flex items-center gap-1.5 px-3 py-1 text-sm font-medium bg-[#FBD037]/10 text-gray-800 rounded-md border border-[#FBD037]/20 max-w-full"
                    >
                      <span className="truncate max-w-[150px] break-all">{tag}</span>
                      <button
                        type="button"
                        className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                        onClick={() => {
                          const newValue = (value as string[]).filter((_, i) => i !== index);
                          onChange(newValue);
                        }}
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="3"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </span>
                  ))}

                  <input
                    type="text"
                    disabled={disabled || field.disabled}
                    placeholder={!value || value.length === 0 ? field.placeholder : ''}
                    className="flex-grow w-0 min-w-[50px] outline-none text-sm bg-transparent disabled:cursor-not-allowed h-7"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        const rawVal = e.currentTarget.value.trim().replace(/,$/, '');
                        if (rawVal) {
                          const currentTags = Array.isArray(value) ? (value as string[]) : [];
                          if (!currentTags.includes(rawVal)) {
                            onChange([...currentTags, rawVal]);
                          }
                          e.currentTarget.value = '';
                        }
                      }
                    }}
                    onBlur={(e) => {
                      const rawVal = e.currentTarget.value.trim().replace(/,$/, '');
                      if (rawVal) {
                        const currentTags = Array.isArray(value) ? (value as string[]) : [];
                        if (!currentTags.includes(rawVal)) {
                          onChange([...currentTags, rawVal]);
                        }
                        e.currentTarget.value = '';
                      }
                      onBlur();
                    }}
                  />
                </div>
              </div>
            ) : field.type === 'select' ? (
              <>
                <label className={labelClass}>
                  {field.label} {asterisk}
                </label>
                <select
                  disabled={disabled || field.disabled}
                  value={value || ''}
                  onChange={(e) => onChange(e.target.value)}
                  onBlur={onBlur}
                  className={`${baseInputClass} ${error ? 'border-red-500' : ''}`}
                >
                  <option value="">Select option...</option>
                  {field.options?.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <>
                <label className={labelClass}>
                  {field.label} {asterisk}
                </label>
                <input
                  type={field.type}
                  disabled={disabled || field.disabled}
                  placeholder={field.placeholder || ''}
                  value={value !== undefined ? value : ''}
                  onChange={(e) => {
                    const val = field.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value;
                    onChange(val);
                  }}
                  onBlur={onBlur}
                  className={`${baseInputClass} ${error ? 'border-red-500' : ''}`}
                />
              </>
            )}

            {error && (
              <span className="text-red-500 text-xs mt-1">
                {(error as any).message || 'This field is required'}
              </span>
            )}
          </div>
        );
      }}
    />
  );
}
